import type { RealtimeVoiceFeatureSettings, RealtimeVoiceFeatureSettingsWrite, DesktopRealtimeVoiceAvailability, DesktopVoiceAvailability } from '../../shared-types/src/realtime.js';
import type { RealtimeVoiceAdminView, RealtimeVoiceAdminWrite, TenantVoiceAdminView, TenantVoicePolicyWrite, VoiceFeatureSettings, VoiceFeatureSettingsWrite, VoiceModelAdminWrite } from "../../shared-types/src/realtime.js";
import type { SessionProtocol, SessionCapabilities, SessionRevokeInput, RotatingTokenPair } from '../../shared-types/src/session.js';
import type { RecommendedModelProfileView, RecommendedModelProfileWrite } from '../../shared-types/src/recommended-models.js';
import type { ModelThinkingBootstrapOptions } from '../../shared-types/src/model-thinking.js';
// Veluga ServiceClient typed surface and route-table compatibility facade.
//
// Route metadata lives in shared-types/src/routes.ts. This file defines:
//   - the read/write surface a Veluga client (desktop main, admin-web, tests)
//     depends on, and
//   - the compatibility SERVICE_ROUTES view used by both transports.
//     Implementations live in `http-client.ts` (fetch) and
//     `in-process-client.ts` (wrapping the service-api in-process).
//
// HARD CONSTRAINT: typed client endpoints update ROUTE_TABLE and ServiceClient
// together. The contract test checks their compatibility view.
//
// Secret-bearing fields (tokens, refresh tokens, gateway tokens, model-route
// credentials, raw backend URLs) MUST NOT be carried on the wire or in the
// typed surface. The service-api enforces redaction; the desktop IPC layer
// re-checks it (`assertNoTokenInPayload`).

import type {
  AdminAuditEvent,
  AdminGroupView,
  AdminOverview,
  AdminUserView,
  AutoRoutingPreviewRequest,
  AutoRoutingPreviewResult,
  AutoRoutingDecisionsView,
  AutoRoutingProfileView,
  AutoRoutingProfileWrite,
  AgentPermissionMode,
  PlatformAdminView,
  PartnerAdminView,
  PartnerOverview,
  AuditListQuery,
  DesktopBootstrap,
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  RequiredConsent,
  DesktopModelView,
  DesktopPlatform,
  CapabilitySourceRecord,
  CapabilitySourceAssignment,
  DownloadChannel,
  DownloadManifest,
  ExtensionManifest,
  GatewayBackend,
  GatewayCompatibility,
  Group,
  Announcement,
  AnnouncementListQuery,
  CreateAnnouncementRequest,
  MarkAllAnnouncementsReadResult,
  MeAnnouncementsResponse,
  PatchAnnouncementRequest,
  InvitedUser,
  ListQuery,
  ModelRoute,
  ModelRoutingPolicy,
  OrganizationSsoConnectionAdmin,
  OrganizationSsoConnectionPatch,
  OrganizationSsoConnectionTestInput,
  OrganizationSsoConnectionTestResult,
  OrganizationSsoConnectionWrite,
  OrganizationSsoDiscovery,
  OrganizationSsoIdentifierType,
  OrganizationSsoMode,
  Paginated,
  PasswordLoginInput,
  PasswordResetCodeAccepted,
  PasswordResetCodeConfirmInput,
  PasswordResetCodeRequestInput,
  PasswordResetCodeResendInput,
  PasswordResetCodeResent,
  PasswordResetCodeVerifyInput,
  PasswordResetCodeVerified,
  CreatePartnerCustomModelRequest,
  PartnerCustomModelCredentialInput,
  PartnerCustomModelDefinition,
  PartnerCustomModelPricing,
  PartnerCustomModelPricingInput,
  PartnerCustomModelDraftTestInput,
  PartnerCustomModelPatch,
  PartnerCustomModelTestRequest,
  PartnerCustomModelTestResult,
  UpdatePartnerCustomModelRequest,
  UpdatePartnerCustomModelResult,
  AllocationOverview,
  CreditAllocation,
  CreditSettlementQuery,
  CreditSettlementReport,
  CreditLimitView,
  EffectiveCreditLimitsRequest,
  EffectiveCreditLimitsView,
  MemberQuotaDefault,
  MembershipQuotaAssignment,
  CreateQuotaProfileRequest,
  QuotaProfileAssignmentPreview,
  QuotaProfileLimitInput,
  QuotaProfileListView,
  QuotaProfileView,
  UpdateQuotaProfileRequest,
  UserQuotaAssignmentQuery,
  UserQuotaAssignmentView,
  Partner,
  UpsertCreditLimitRequest,
  PlatformProviderKeyProvider,
  PlatformProviderModelDefinition,
  PlatformProviderModelListQuery,
  BulkDeployPlatformProviderModelsRequest,
  BulkDeployPlatformProviderModelsResult,
  CreatePlatformProviderModelRequest,
  DeployPlatformProviderModelRequest,
  DeployPlatformProviderModelResult,
  PlatformProviderModelDeployment,
  PlatformProviderModelTestResult,
  PublishPlatformProviderModelRatesRequest,
  PublishPlatformProviderModelRatesResult,
  UpdatePlatformProviderModelRequest,
  PlatformWebSearchKeySummary,
  PartnerWebSearchUsageSummary,
  ProviderKeyRegistrationResult,
  ProviderKeyRemovalResult,
  ProviderKeySummary,
  ProviderKeyTestResult,
  PolicyContextSnapshot,
  QuotaPolicy,
  RouteDecisionEvent,
  ServiceRole,
  ServiceUser,
  SocialLoginMode,
  SocialProviderConfig,
  SocialProviderKind,
  SocialProviderPublic,
  SignupConfig,
  SignupAccepted,
  SignupInput,
  Tenant,
  TenantPartnerTransferResult,
  TenantSelfSignupResult,
  TenantOrganizationSsoConfig,
  TenantAssignment,
  TenantLoginDefaultModelView,
  TenantMcpConnectors,
  TenantMediaRoute,
  TenantMediaRouteSummary,
  TenantSocialConfig,
  TenantSocialProvider,
  TenantModelAccess,
  TenantWebSearchBackend,
  TenantWebSearchBackendSummary,
  TestLiteLLMConnectionInput,
  TestProviderKeyInput,
  DeploymentProfile,
  DefaultSignupTenantSetting,
  CreditRateTableView,
  CreditRateSettingsView,
  PutCreditRateSettingsRequest,
  PutCreditRateSettingsResult,
  UsageChainVerification,
  UsageEventView,
  ExtensionReleaseView,
  UsageQuery,
  UsageSeriesPoint,
  UsageSeriesQuery,
  UsageSummaryView,
  UserListQuery,
  VelugaModelCatalogItem,
  WebSearchNativeProvider,
  BillingHistoryPage,
  CardRegistrationStart,
  CardRegistrationView,
  CreateCreditOrderInput,
  CreditPurchaseOrderView,
  MyBillingSummary,
  ResolvedCreditOffer,
} from '../../shared-types/src/index.js';
import type { MergePolicyInput } from '../../policy-service/src/merge.js';
import { ROUTE_TABLE, clientRoutes } from '../../shared-types/src/routes.js';

// ── Auth shapes ───────────────────────────────────────────────────────────

/** Login start — the browser/CLI follow-up opens `verificationUri` to
 *  authorize the pending `loginHandle`; the device code is then exchanged at
 *  `/v1/auth/login/complete`. */
export interface LoginStartResult {
  loginHandle: string;
  verificationUri: string;
}

/** Invite-bound login input. The desktop OAuth-style flow omits this and
 *  relies on the browser authorize step (`authorizeLogin`). */
export interface InviteLoginInput {
  sessionProtocol?: SessionProtocol;
  tenantId: string;
  userId: string;
  inviteToken: string;
}

export interface TokenPair {
  sessionProtocol?: SessionProtocol;
  refreshExpiresAt?: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

export interface AccessTokenResult {
  accessToken: string;
  expiresAt: string;
}

export interface ActAsSessionResult {
  accessToken: string;
  expiresAt: string;
  tenantId: string;
  roles: ServiceRole[];
}

/** Credit allocation write payload (the grantee is supplied per call). */
export interface AllocationInput {
  creditMillis?: number;
  inputTokens?: number;
  outputTokens?: number;
  unlimited?: boolean;
  note?: string;
  idempotencyKey: string;
}

export interface AllocationBatchInput {
  idempotencyKey: string;
  items: Array<
    Omit<AllocationInput, 'idempotencyKey' | 'unlimited'> & {
      granteeKind: 'group' | 'user';
      granteeId: string;
    }
  >;
}

export interface AllocationBatchResult {
  results: Array<{
    index: number;
    allocation: CreditAllocation;
  }>;
}

export type MemberQuotaDefaultInput = Pick<
  MemberQuotaDefault,
  'nodeKind' | 'nodeId' | 'perMemberCreditMillis' | 'mode'
> & {
  /** Omitted = 'lifetime', the pre-cycle contract. */
  cycle?: MemberQuotaDefault['cycle'];
  idempotencyKey: string;
};

// ── Redacted admin views ──────────────────────────────────────────────────

export interface ModelRouteAdminView {
  id: string;
  tenantId: string;
  catalogModelId: string;
  routeKind: ModelRoute['routeKind'];
  providerProtocol: ModelRoute['providerProtocol'];
  managedBy: ModelRoute['managedBy'];
  status: ModelRoute['status'];
  hasEndpoint: boolean;
  hasCredential: boolean;
}

export interface GatewayBackendAdminView {
  id: string;
  tenantId?: string;
  kind: GatewayBackend['kind'];
  displayName: string;
  deploymentProfile: GatewayBackend['deploymentProfile'];
  managedBy: GatewayBackend['managedBy'];
  status: GatewayBackend['status'];
  telemetryMode: GatewayBackend['telemetryMode'];
  hasBaseUrl: boolean;
  hasCredential: boolean;
}

export interface InvitedUserResult {
  user: ServiceUser;
  inviteToken: string;
  expiresAt: string;
}

export interface CreatedUserResult {
  user: ServiceUser;
}

/** Re-export of shared `InvitedUser` so the in-process client and admin
 *  web imports both reach the same definition. */
export type { InvitedUser };

/** Re-exported so admin-web reaches one definition of the credit-limit shapes. */
export type {
  CreditLimitView,
  EffectiveCreditLimitsRequest,
  EffectiveCreditLimitsView,
  UpsertCreditLimitRequest,
  CreateQuotaProfileRequest,
  MembershipQuotaAssignment,
  QuotaProfileAssignmentPreview,
  QuotaProfileLimitInput,
  QuotaProfileListView,
  QuotaProfileView,
  UpdateQuotaProfileRequest,
  UserQuotaAssignmentQuery,
  UserQuotaAssignmentView,
};

/** Re-exported so admin-web's three announcement consoles and the desktop panel
 *  all reach one definition. */
export type {
  Announcement,
  AnnouncementListQuery,
  CreateAnnouncementRequest,
  MarkAllAnnouncementsReadResult,
  MeAnnouncementsResponse,
  PatchAnnouncementRequest,
};

export interface ModelAccessSubject {
  subjectType: 'user' | 'group';
  subjectId: string;
  allowedModelIds: string[];
  defaultModelId: string;
}

/** Re-exported so admin-web and service-api reach one definition of the
 *  partner custom-model pricing and edit contracts (declared in shared-types
 *  alongside the platform provider-model siblings). */
export type {
  CreatePartnerCustomModelRequest,
  PartnerCustomModelCredentialInput,
  PartnerCustomModelDraftTestInput,
  PartnerCustomModelPricing,
  PartnerCustomModelPricingInput,
  PartnerCustomModelPatch,
  PartnerCustomModelTestRequest,
  PartnerCustomModelTestResult,
  UpdatePartnerCustomModelRequest,
  UpdatePartnerCustomModelResult,
};

/** Redacted read shape. `pricing` and `revision` are exposed as stored — they
 *  carry no secrets and the UI needs both (revision for compare-and-swap). */
export type PartnerCustomModelSummary = Omit<
  PartnerCustomModelDefinition,
  'routeBackend'
> & {
  hasRouteBackend: boolean;
  /** The stored provider endpoint, for the platform console's edit form.
   *
   *  Present only on the platform list read, only for a service-generated
   *  credential backend, and only when the stored URL carries no userinfo, query,
   *  or fragment — see `isEchoableProviderBaseUrl`. Anything else is omitted, so
   *  a blank field means "unknown or not safe to show", never "cleared". */
  providerBaseUrl?: string;
};
/** `revision` is omitted because it is server-owned: leaving it in would make
 *  it a client-settable field and `normalizePartnerCustomModel`'s payload
 *  spread would carry a forged value straight into the stored definition,
 *  bypassing compare-and-swap. `pricing` is omitted from the stored shape and
 *  re-declared as the input shape, whose `marginMultiplier` is optional. */
export type PartnerCustomModelWrite = Omit<
  PartnerCustomModelDefinition,
  'partnerId' | 'createdAt' | 'updatedAt' | 'status' | 'revision' | 'pricing' | 'billingMode'
> & {
  status?: PartnerCustomModelDefinition['status'];
  /** Required. `billingMode` is derived from these rates, which is why it is no
   *  longer an input: an operator-picked mode disconnected from settlement was
   *  the bug this contract replaces. Both rates 0 means free. */
  pricing: PartnerCustomModelPricingInput;
  providerCredential?: PartnerCustomModelCredentialInput;
};
export interface PartnerCustomModelDeployment {
  catalogModelId: string;
}

// ── The HTTP route table — canonical method/path contract ────────────────

export { ROUTE_TABLE };
export const SERVICE_ROUTES = clientRoutes(ROUTE_TABLE);

/** @deprecated Use ROUTE_TABLE descriptor access metadata instead. */
export const AUTH_REQUIRED_METHODS: ReadonlySet<keyof typeof SERVICE_ROUTES> =
  new Set(
    Object.entries(ROUTE_TABLE)
      .filter(
        ([, route]) =>
          route.access === 'bearer' && !('serverOnly' in route)
      )
      .map(([key]) => key as keyof typeof SERVICE_ROUTES)
  );

/**
 * The full read/write surface of the Veluga Service API. Two transports
 * implement it: `HttpServiceClient` (admin-web) and `InProcessServiceClient`
 * (desktop main, tests). RBAC is enforced server-side; the typed surface
 * is the same for every role.
 *
 * The two auth shapes coexist on purpose:
 *   - `loginStart()` / `loginComplete(handle)` — desktop OAuth-device flow
 *   - `loginStart({invite})` / `loginComplete({invite})` — admin/operator
 *     invite redeem (no separate browser step).
 */
export interface VoiceRequestOptions { signal?: AbortSignal; timeoutMs?: number }

export interface ServiceClient {
  getRealtimeVoiceSettings(accessToken: string): Promise<RealtimeVoiceFeatureSettings>;
  putRealtimeVoiceSettings(accessToken: string, input: RealtimeVoiceFeatureSettingsWrite, idempotencyKey: string): Promise<RealtimeVoiceFeatureSettings>;
  getMyRealtimeVoice(accessToken: string, options?: VoiceRequestOptions): Promise<DesktopRealtimeVoiceAvailability>;
  getMyVoiceAvailability(accessToken: string, options?: VoiceRequestOptions): Promise<DesktopVoiceAvailability>;
  sessionCapabilities(): Promise<SessionCapabilities>;
  refreshSession(refreshToken: string): Promise<RotatingTokenPair>;
  revokeSession(input: SessionRevokeInput): Promise<{ ok: true }>;
  // ── auth ────────────────────────────────────────────────────────────
  /** `opts.providerId` switches to the social-login flow: the returned
   *  verificationUri points at that provider's SSO /start (PR-5). */
  loginStart(
    invite?: InviteLoginInput,
    opts?: { providerId?: string; sessionProtocol?: SessionProtocol }
  ): Promise<LoginStartResult>;
  loginComplete(input: string | InviteLoginInput): Promise<TokenPair>;
  /** Public (no auth): active social providers for a tenant's login screen.
   *  Empty unless the tenant is `optional` with enabled providers (R-2). */
  listSocialProviders(tenantHint?: string): Promise<SocialProviderPublic[]>;
  getSignupConfig(): Promise<SignupConfig>;
  signup(input: SignupInput): Promise<SignupAccepted>;
  requestPasswordResetCode(input: PasswordResetCodeRequestInput): Promise<PasswordResetCodeAccepted>;
  resendPasswordResetCode(input: PasswordResetCodeResendInput): Promise<PasswordResetCodeResent>;
  verifyPasswordResetCode(input: PasswordResetCodeVerifyInput): Promise<PasswordResetCodeVerified>;
  confirmPasswordResetCode(input: PasswordResetCodeConfirmInput): Promise<void>;
  listOrganizationSsoConnections(
    tenantHint?: string
  ): Promise<OrganizationSsoDiscovery>;
  organizationSsoLoginStart(input: {
    sessionProtocol?: SessionProtocol;
    connectionId: string;
    identifierType?: OrganizationSsoIdentifierType;
    identifier?: string;
  }): Promise<LoginStartResult>;
  loginWithPassword(input: PasswordLoginInput): Promise<TokenPair>;
  changePassword(
    accessToken: string,
    oldPassword: string,
    newPassword: string
  ): Promise<TokenPair | void>;
  keepPassword(accessToken: string): Promise<void>;
  refresh(refreshToken: string): Promise<AccessTokenResult>;

  // ── self / desktop bootstrap ────────────────────────────────────────
  bootstrap(accessToken: string, options?: ModelThinkingBootstrapOptions): Promise<DesktopBootstrap>;
  getMyBillingSummary(accessToken: string): Promise<MyBillingSummary>;
  listMyCreditProducts(accessToken: string): Promise<ResolvedCreditOffer[]>;
  startCardRegistration(accessToken: string): Promise<CardRegistrationStart>;
  getCardRegistration(
    accessToken: string,
    registrationId: string
  ): Promise<CardRegistrationView>;
  deleteMyCard(accessToken: string): Promise<void>;
  createCreditOrder(
    accessToken: string,
    input: CreateCreditOrderInput
  ): Promise<CreditPurchaseOrderView>;
  payCreditOrder(
    accessToken: string,
    orderId: string
  ): Promise<CreditPurchaseOrderView>;
  getCreditOrder(
    accessToken: string,
    orderId: string
  ): Promise<CreditPurchaseOrderView>;
  listMyBillingHistory(
    accessToken: string,
    input: { page: number; pageSize: number }
  ): Promise<BillingHistoryPage>;
  acceptConsents(
    accessToken: string,
    input: { consents: RequiredConsent[] }
  ): Promise<{ pendingConsents: RequiredConsent[] }>;
  uploadDiagnosticBundle(
    accessToken: string,
    bytes: Uint8Array,
    meta: {
      deviceId: string;
      appVersion: string;
      platform: 'win32' | 'darwin' | 'linux';
      collectedAt: string;
      logFileCount: number;
    }
  ): Promise<{ id: string; uploadedAt: string }>;
  catalog(accessToken: string): Promise<DesktopModelView[]>;
  availableModels(accessToken: string): Promise<DesktopModelView[]>;
  policiesEffective(accessToken: string): Promise<PolicyContextSnapshot>;
  postClientEvent(
    accessToken: string,
    event: Record<string, unknown>
  ): Promise<void>;

  // ── downloads ──────────────────────────────────────────────────────
  downloadChannels(accessToken: string): Promise<DownloadChannel[]>;
  downloadLatest(
    accessToken: string,
    channel: DownloadChannel,
    platform: DesktopPlatform
  ): Promise<DownloadManifest>;
  extensionLatest(
    accessToken: string,
    extensionId: string,
    platform: 'win32' | 'darwin',
    request: { appVersion: string; protocolVersion: number }
  ): Promise<ExtensionManifest>;

  // ── gateway facade capabilities ────────────────────────────────────
  gatewayCapabilities(accessToken: string): Promise<GatewayCompatibility>;

  // ── platform: read/write ───────────────────────────────────────────
  platformLoginWithPassword(input: {
    sessionProtocol?: SessionProtocol;
    email: string;
    password: string;
  }): Promise<TokenPair>;
  /** Without `query` → legacy full array; with `query` → `Paginated` envelope
   *  (same dual-mode contract on `listUsers`/`listAudit`). */
  listTenants(accessToken: string): Promise<Tenant[]>;
  listTenants(
    accessToken: string,
    query: ListQuery
  ): Promise<Paginated<Tenant>>;
  createTenant(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      partnerId?: string;
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  deleteTenant(
    accessToken: string,
    input: { tenantId: string; confirmName: string; idempotencyKey: string }
  ): Promise<void>;
  transferTenantPartner(
    accessToken: string,
    input: {
      tenantId: string;
      partnerId: string | null;
      confirmName?: string;
      dryRun?: boolean;
      idempotencyKey: string;
    }
  ): Promise<TenantPartnerTransferResult>;
  actAsTenant(
    accessToken: string,
    tenantId: string
  ): Promise<ActAsSessionResult>;
  getAutoRoutingProfile(accessToken: string, tenantId: string): Promise<AutoRoutingProfileView>;
  getRealtimeVoice(accessToken: string, tenantId: string): Promise<RealtimeVoiceAdminView>;
  putRealtimeVoice(accessToken: string, tenantId: string, input: RealtimeVoiceAdminWrite, idempotencyKey: string): Promise<RealtimeVoiceAdminView>;
  getVoiceSettings(accessToken: string): Promise<VoiceFeatureSettings>;
  putVoiceSettings(accessToken: string, input: VoiceFeatureSettingsWrite, idempotencyKey: string): Promise<VoiceFeatureSettings>;
  getTenantVoice(accessToken: string, tenantId: string): Promise<TenantVoiceAdminView>;
  putTenantVoice(accessToken: string, tenantId: string, input: TenantVoicePolicyWrite, idempotencyKey: string): Promise<TenantVoiceAdminView>;
  postTenantVoiceModel(accessToken: string, tenantId: string, input: VoiceModelAdminWrite, idempotencyKey: string): Promise<TenantVoiceAdminView>;
  getRecommendedModelProfile(accessToken: string, tenantId: string): Promise<RecommendedModelProfileView>;
  putRecommendedModelProfile(accessToken: string, input: { tenantId: string; profile: RecommendedModelProfileWrite; expectedRevision: number; idempotencyKey: string }): Promise<RecommendedModelProfileView>;
  putAutoRoutingProfile(
    accessToken: string,
    input: { tenantId: string; profile: AutoRoutingProfileWrite; idempotencyKey: string }
  ): Promise<AutoRoutingProfileView>;
  previewAutoRouting(
    accessToken: string,
    input: { tenantId: string; preview: AutoRoutingPreviewRequest }
  ): Promise<AutoRoutingPreviewResult>;
  listAutoRoutingDecisions(
    accessToken: string,
    tenantId: string,
    limit?: number
  ): Promise<AutoRoutingDecisionsView>;
  registerProviderKey(
    accessToken: string,
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      apiKey: string;
      baseUrl?: string;
      litellmBaseUrl?: string;
      idempotencyKey: string;
    }
  ): Promise<ProviderKeyRegistrationResult>;
  listPlatformProviderModels(
    accessToken: string,
    query?: PlatformProviderModelListQuery
  ): Promise<Paginated<PlatformProviderModelDefinition>>;
  getPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string
  ): Promise<PlatformProviderModelDefinition>;
  createPlatformProviderModel(
    accessToken: string,
    input: CreatePlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<PlatformProviderModelDefinition>;
  updatePlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: UpdatePlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<PlatformProviderModelDefinition>;
  disablePlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    idempotencyKey: string
  ): Promise<PlatformProviderModelDefinition>;
  testPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string
  ): Promise<PlatformProviderModelTestResult>;
  deployPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: DeployPlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<DeployPlatformProviderModelResult>;
  bulkDeployPlatformProviderModels(
    accessToken: string,
    tenantId: string,
    input: BulkDeployPlatformProviderModelsRequest & { idempotencyKey: string }
  ): Promise<BulkDeployPlatformProviderModelsResult>;
  publishPlatformProviderModelRates(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: PublishPlatformProviderModelRatesRequest & { idempotencyKey: string }
  ): Promise<PublishPlatformProviderModelRatesResult>;
  revokePlatformProviderModelDeployment(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string,
    idempotencyKey: string
  ): Promise<PlatformProviderModelDeployment>;
  removeProviderKey(
    accessToken: string,
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      idempotencyKey: string;
    }
  ): Promise<ProviderKeyRemovalResult>;
  getProviderKeys(
    accessToken: string,
    tenantId: string
  ): Promise<ProviderKeySummary[]>;
  listTenantWebSearchBackends(
    accessToken: string,
    tenantId: string
  ): Promise<TenantWebSearchBackendSummary[]>;
  listTenantMediaRoutes(accessToken: string, tenantId: string): Promise<TenantMediaRouteSummary[]>;
  getPlatformWebSearchKey(
    accessToken: string
  ): Promise<PlatformWebSearchKeySummary>;
  setPlatformWebSearchKey(
    accessToken: string,
    input: { apiKey: string; idempotencyKey: string }
  ): Promise<PlatformWebSearchKeySummary>;
  setTenantWebSearchBackend(
    accessToken: string,
    input: {
      tenantId: string;
      provider: WebSearchNativeProvider;
      status: TenantWebSearchBackend['status'];
      maxUsesDefault?: number;
      idempotencyKey: string;
    }
  ): Promise<TenantWebSearchBackendSummary>;
  setTenantMediaRoute(
    accessToken: string,
    input: {
      tenantId: string;
      kind: TenantMediaRoute['kind'];
      provider: TenantMediaRoute['provider'];
      status: TenantMediaRoute['status'];
      model: string;
      endpointRegion?: string;
      supportsZdr: boolean;
      supportsMam: boolean;
      providerOptions?: Record<string, unknown>;
      maxUsesDefault?: number;
      requiredDataRegion?: string;
      requireZdr: boolean;
      requireMam: boolean;
      externalApis: 'allow' | 'deny';
      idempotencyKey: string;
    }
  ): Promise<TenantMediaRouteSummary>;
  testProviderKey(
    accessToken: string,
    input: TestProviderKeyInput
  ): Promise<ProviderKeyTestResult>;
  testLiteLLMConnection(
    accessToken: string,
    input: TestLiteLLMConnectionInput
  ): Promise<ProviderKeyTestResult>;
  getTenantCatalog(
    accessToken: string,
    tenantId: string
  ): Promise<DesktopModelView[]>;
  getTenantAssignment(
    accessToken: string,
    tenantId: string
  ): Promise<TenantAssignment>;
  setTenantAssignment(
    accessToken: string,
    input: {
      tenantId: string;
      allowedModelIds: string[];
      defaultMemberModelIds?: string[];
      freeTierModelIds?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantAssignment>;
  getCurrentRates(accessToken: string): Promise<CreditRateTableView | undefined>;
  getRateSettings(accessToken: string): Promise<CreditRateSettingsView>;
  putRateSettings(
    accessToken: string,
    input: PutCreditRateSettingsRequest & { idempotencyKey: string }
  ): Promise<PutCreditRateSettingsResult>;
  adoptPublishedRateSettings(accessToken: string, idempotencyKey: string): Promise<CreditRateSettingsView>;

  // ── platform: partners ─────────────────────────────────────────────
  listPartners(accessToken: string): Promise<Partner[]>;
  createPartner(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      tenantCap?: number;
      idempotencyKey: string;
    }
  ): Promise<Partner>;
  getPartner(accessToken: string, partnerId: string): Promise<Partner>;
  updatePartner(
    accessToken: string,
    input: {
      partnerId: string;
      tenantCap?: number | null;
      status?: 'active' | 'suspended';
      allowCustomModels?: boolean;
      idempotencyKey: string;
    }
  ): Promise<Partner>;
  deletePartner(
    accessToken: string,
    input: { partnerId: string; confirmName: string; idempotencyKey: string }
  ): Promise<void>;
  setTenantCustomModels(
    accessToken: string,
    input: {
      tenantId: string;
      allowCustomModels: boolean;
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  setTenantSelfSignup(
    accessToken: string,
    input: { tenantId: string; allowed?: boolean; enabled?: boolean; idempotencyKey: string }
  ): Promise<TenantSelfSignupResult>;
  getDefaultSignupTenant(accessToken: string): Promise<DefaultSignupTenantSetting>;
  setDefaultSignupTenant(
    accessToken: string,
    input: { tenantId: string | null; idempotencyKey: string }
  ): Promise<DefaultSignupTenantSetting>;
  assignTenantCapabilitySource(
    accessToken: string,
    input: { tenantId: string; sourceId: string; idempotencyKey: string }
  ): Promise<CapabilitySourceAssignment>;
  revokeTenantCapabilitySource(
    accessToken: string,
    input: { tenantId: string; sourceId: string; idempotencyKey: string }
  ): Promise<void>;
  setTenantCustomerModelLabels(
    accessToken: string,
    input: {
      tenantId: string;
      labels: Record<string, { providerName?: string; modelName?: string }>;
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  setTenantMcpConnectors(
    accessToken: string,
    input: {
      tenantId: string;
      activeMcpConnectors: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  setTenantPermissionModes(
    accessToken: string,
    input: {
      tenantId: string;
      allowedPermissionModes: AgentPermissionMode[];
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  setTenantDeepAgentApprovalTools(
    accessToken: string,
    input: {
      tenantId: string;
      deepAgentApprovalTools: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  getTenantMcpConnectors(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMcpConnectors>;
  getOwnMcpConnectors(accessToken: string): Promise<TenantMcpConnectors>;
  // ── social login (SSO) admin ──────────────────────────────────────────
  getSocialConfig(
    accessToken: string,
    tenantId: string
  ): Promise<TenantSocialConfig>;
  updateSocialMode(
    accessToken: string,
    input: {
      tenantId: string;
      socialLoginMode: SocialLoginMode;
      idempotencyKey: string;
    }
  ): Promise<TenantSocialConfig>;
  createSocialProvider(
    accessToken: string,
    input: {
      tenantId: string;
      provider: SocialProviderKind;
      displayName: string;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantSocialProvider>;
  updateSocialProvider(
    accessToken: string,
    input: {
      tenantId: string;
      providerId: string;
      displayName?: string;
      enabled?: boolean;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantSocialProvider>;
  deleteSocialProvider(
    accessToken: string,
    input: { tenantId: string; providerId: string; idempotencyKey: string }
  ): Promise<void>;
  getOrganizationSsoConfig(
    accessToken: string,
    tenantId: string
  ): Promise<TenantOrganizationSsoConfig>;
  updateOrganizationSsoMode(
    accessToken: string,
    input: {
      tenantId: string;
      mode: OrganizationSsoMode;
      confirmBreakGlass?: boolean;
      idempotencyKey: string;
    }
  ): Promise<TenantOrganizationSsoConfig>;
  createOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connection: OrganizationSsoConnectionWrite;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin>;
  updateOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      patch: OrganizationSsoConnectionPatch;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin>;
  deleteOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      idempotencyKey: string;
    }
  ): Promise<void>;
  testOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      test?: OrganizationSsoConnectionTestInput;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionTestResult>;
  setOrganizationSsoConnectionEnabled(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      enabled: boolean;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin>;
  getOrganizationSsoSamlMetadata(
    accessToken: string,
    tenantId: string,
    connectionId: string
  ): Promise<string>;
  listPartnerAdmins(
    accessToken: string,
    partnerId: string
  ): Promise<PartnerAdminView[]>;
  grantPartnerAdmin(
    accessToken: string,
    input: {
      partnerId: string;
      email: string;
      name: string;
      password: string;
      idempotencyKey: string;
    }
  ): Promise<{ partnerId: string; userId: string }>;
  revokePartnerAdmin(
    accessToken: string,
    input: { partnerId: string; userId: string; idempotencyKey: string }
  ): Promise<void>;
  getPartnerModelScope(
    accessToken: string,
    partnerId: string
  ): Promise<string[]>;
  setPartnerModelScope(
    accessToken: string,
    input: { partnerId: string; modelKeys: string[]; idempotencyKey: string }
  ): Promise<string[]>;
  listPartnerCustomModels(
    accessToken: string,
    partnerId: string
  ): Promise<PartnerCustomModelSummary[]>;
  registerPartnerCustomModel(
    accessToken: string,
    input: {
      partnerId: string;
      definition: PartnerCustomModelWrite;
      idempotencyKey: string;
    }
  ): Promise<PartnerCustomModelSummary>;
  /** Merge-patch an existing definition under compare-and-swap on `revision`,
   *  resyncing every active tenant deployment in the same transaction. */
  patchPartnerCustomModel(
    accessToken: string,
    input: {
      partnerId: string;
      modelKey: string;
      request: UpdatePartnerCustomModelRequest;
      idempotencyKey: string;
    }
  ): Promise<UpdatePartnerCustomModelResult>;
  deletePartnerCustomModel(
    accessToken: string,
    input: { partnerId: string; modelKey: string; idempotencyKey: string }
  ): Promise<void>;
  /** Diagnostics. No idempotency key: a retry must re-hit the network, matching
   *  `testProviderKey`/`testLiteLLMConnection`. Rate limiting, not replay, is
   *  what bounds repeat calls. */
  testPartnerCustomModelProvider(
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult>;
  testPartnerCustomModelLlm(
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult>;
  /** Deploys a partner custom model definition into a partner-owned tenant
   *  through the platform-only operator path. */
  platformDeployCustomModel(
    accessToken: string,
    input: { tenantId: string; customModelKey: string; idempotencyKey: string }
  ): Promise<PartnerCustomModelDeployment>;
  platformRevokeCustomModel(
    accessToken: string,
    input: { tenantId: string; customModelKey: string; idempotencyKey: string }
  ): Promise<void>;
  getPartnerMcpConnectorScope(
    accessToken: string,
    partnerId: string
  ): Promise<string[]>;
  setPartnerMcpConnectorScope(
    accessToken: string,
    input: {
      partnerId: string;
      connectorIds: string[];
      idempotencyKey: string;
    }
  ): Promise<string[]>;

  // ── partner console (scope==='partner') ────────────────────────────
  partnerLoginWithPassword(input: {
    sessionProtocol?: SessionProtocol;
    email: string;
    password: string;
  }): Promise<TokenPair & { partnerId: string }>;
  partnerSelf(accessToken: string): Promise<Partner>;
  partnerOverview(accessToken: string, query?: Pick<UsageQuery, 'from' | 'to'>): Promise<PartnerOverview>;
  partnerListAdmins(accessToken: string): Promise<PartnerAdminView[]>;
  partnerGrantAdmin(accessToken: string, input: { email: string; name: string; password?: string; idempotencyKey: string }): Promise<{ partnerId: string; userId: string }>;
  partnerRevokeAdmin(accessToken: string, input: { userId: string; idempotencyKey: string }): Promise<void>;
  partnerSelfModelScope(accessToken: string): Promise<string[]>;
  partnerListCustomModels(
    accessToken: string
  ): Promise<PartnerCustomModelSummary[]>;
  partnerListTenants(accessToken: string): Promise<Tenant[]>;
  partnerCreateTenant(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  partnerActAsTenant(
    accessToken: string,
    tenantId: string
  ): Promise<ActAsSessionResult>;
  partnerGetTenantAssignment(
    accessToken: string,
    tenantId: string
  ): Promise<TenantAssignment>;
  partnerSetTenantAssignment(
    accessToken: string,
    input: {
      tenantId: string;
      allowedModelIds: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantAssignment>;
  partnerGetTenantMcpConnectors(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMcpConnectors>;
  partnerSetTenantMcpConnectors(
    accessToken: string,
    input: {
      tenantId: string;
      activeMcpConnectors: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  partnerSetTenantSelfSignup(
    accessToken: string,
    input: { tenantId: string; enabled: boolean; idempotencyKey: string }
  ): Promise<TenantSelfSignupResult>;
  partnerSetTenantPermissionModes(
    accessToken: string,
    input: {
      tenantId: string;
      allowedPermissionModes: AgentPermissionMode[];
      idempotencyKey: string;
    }
  ): Promise<Tenant>;
  partnerSetTenantDeepAgentApprovalTools(
    accessToken: string,
    input: {
      tenantId: string;
      deepAgentApprovalTools: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant>;

  // ── credit allocation (Phase 2) ────────────────────────────────────
  setTenantAllocation(
    accessToken: string,
    input: AllocationInput & { tenantId: string }
  ): Promise<CreditAllocation>;
  getTenantAllocation(
    accessToken: string,
    tenantId: string
  ): Promise<AllocationOverview>;
  setPartnerAllocation(
    accessToken: string,
    input: AllocationInput & { partnerId: string }
  ): Promise<CreditAllocation>;
  getPartnerAllocation(
    accessToken: string,
    partnerId: string
  ): Promise<AllocationOverview>;
  getPartnerWebSearchUsage(
    accessToken: string,
    partnerId: string,
    query?: Pick<UsageQuery, 'from' | 'to'>
  ): Promise<PartnerWebSearchUsageSummary>;
  partnerGetOwnAllocation(accessToken: string): Promise<AllocationOverview>;
  partnerGetOwnWebSearchUsage(
    accessToken: string,
    query?: Pick<UsageQuery, 'from' | 'to'>
  ): Promise<PartnerWebSearchUsageSummary>;
  partnerSetTenantAllocation(
    accessToken: string,
    input: AllocationInput & { tenantId: string }
  ): Promise<CreditAllocation>;
  partnerGetTenantAllocation(
    accessToken: string,
    tenantId: string
  ): Promise<AllocationOverview>;
  tenantGetOwnAllocation(accessToken: string): Promise<AllocationOverview>;
  setSubjectAllocation(
    accessToken: string,
    input: AllocationInput & {
      granteeKind: 'group' | 'user';
      granteeId: string;
    }
  ): Promise<CreditAllocation>;
  setSubjectAllocationsBatch(
    accessToken: string,
    input: AllocationBatchInput
  ): Promise<AllocationBatchResult>;
  getSubjectAllocation(
    accessToken: string,
    granteeKind: 'group' | 'user',
    granteeId: string
  ): Promise<AllocationOverview>;
  setMemberQuotaDefault(
    accessToken: string,
    input: MemberQuotaDefaultInput
  ): Promise<MemberQuotaDefault>;
  getMemberQuotaDefault(
    accessToken: string,
    nodeKind: MemberQuotaDefault['nodeKind'],
    nodeId: string
  ): Promise<MemberQuotaDefault | null>;
  listCreditLimits(
    accessToken: string
  ): Promise<{ items: CreditLimitView[]; advancedPolicyCount: number }>;
  upsertCreditLimit(
    accessToken: string,
    input: UpsertCreditLimitRequest & { idempotencyKey: string }
  ): Promise<CreditLimitView>;
  previewEffectiveCreditLimits(
    accessToken: string,
    input: EffectiveCreditLimitsRequest
  ): Promise<EffectiveCreditLimitsView>;
  deleteCreditLimit(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<{ ok: true; deleted: boolean }>;
  // ── announcements ───────────────────────────────────────────────────────
  // Five operations per authoring surface with IDENTICAL shapes — the method
  // name carries the surface so a console cannot accidentally address another
  // one, and PR-2's shared component takes whichever triple it is given.
  listAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>>;
  createAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement>;
  patchAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  publishAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  archiveAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  listPartnerAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>>;
  createPartnerAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement>;
  patchPartnerAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  publishPartnerAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  archivePartnerAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  listPlatformAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>>;
  createPlatformAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement>;
  patchPlatformAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  publishPlatformAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  archivePlatformAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement>;
  platformDiagnosticBundles(
    accessToken: string,
    query?: DiagnosticBundleListQuery
  ): Promise<Paginated<DiagnosticBundleView>>;
  platformDiagnosticBundleDownload(
    accessToken: string,
    id: string
  ): Promise<{ url: string; expiresAt: string }>;
  /** Member surface: the visible union of all three scopes. */
  listMyAnnouncements(
    accessToken: string,
    params?: { limit?: number; offset?: number }
  ): Promise<MeAnnouncementsResponse>;
  markAnnouncementRead(accessToken: string, id: string): Promise<{ ok: true }>;
  markAllAnnouncementsRead(
    accessToken: string
  ): Promise<MarkAllAnnouncementsReadResult>;
  listQuotaProfiles(accessToken: string): Promise<QuotaProfileListView>;
  createQuotaProfile(
    accessToken: string,
    input: CreateQuotaProfileRequest & { idempotencyKey: string }
  ): Promise<QuotaProfileView>;
  updateQuotaProfile(
    accessToken: string,
    input: UpdateQuotaProfileRequest & { id: string; idempotencyKey: string }
  ): Promise<QuotaProfileView>;
  deactivateQuotaProfile(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<QuotaProfileView>;
  previewQuotaProfileImpact(
    accessToken: string,
    input: {
      id: string;
      limits?: QuotaProfileLimitInput[];
      status?: 'active' | 'inactive';
    }
  ): Promise<QuotaProfileAssignmentPreview>;
  listUserQuotaAssignments(
    accessToken: string,
    query?: UserQuotaAssignmentQuery
  ): Promise<Paginated<UserQuotaAssignmentView>>;
  assignQuotaProfile(
    accessToken: string,
    input: { userId: string; quotaProfileId: string; idempotencyKey: string }
  ): Promise<MembershipQuotaAssignment>;
  clearQuotaProfilePin(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<MembershipQuotaAssignment | null>;

  // ── admin: read ────────────────────────────────────────────────────
  overview(accessToken: string): Promise<AdminOverview>;
  listUsers(accessToken: string): Promise<AdminUserView[]>;
  listUsers(
    accessToken: string,
    query: UserListQuery
  ): Promise<Paginated<AdminUserView>>;
  listGroups(accessToken: string): Promise<AdminGroupView[]>;
  listModelAccess(accessToken: string): Promise<TenantModelAccess[]>;
  getTenantLoginDefaultModel(accessToken: string): Promise<TenantLoginDefaultModelView>;
  previewModels(accessToken: string): Promise<DesktopModelView[]>;
  listModelRoutes(accessToken: string): Promise<ModelRouteAdminView[]>;
  listGatewayBackends(accessToken: string): Promise<GatewayBackendAdminView[]>;
  previewRoutes(accessToken: string): Promise<{
    candidates: ModelRouteAdminView[];
    decisions?: RouteDecisionEvent[];
  }>;
  listRoutesHealth(
    accessToken: string
  ): Promise<{ backends: Array<{ id: string; status: string }> }>;
  previewPolicy(
    accessToken: string,
    draft: MergePolicyInput
  ): Promise<PolicyContextSnapshot>;
  listAudit(accessToken: string): Promise<AdminAuditEvent[]>;
  listAudit(
    accessToken: string,
    query: AuditListQuery
  ): Promise<Paginated<AdminAuditEvent>>;
  exportAudit(accessToken: string): Promise<AdminAuditEvent[]>;
  usageSummary(
    accessToken: string,
    query?: UsageQuery
  ): Promise<UsageSummaryView>;
  usageSeries(
    accessToken: string,
    query?: UsageSeriesQuery
  ): Promise<UsageSeriesPoint[]>;
  usageEvents(
    accessToken: string,
    query?: UsageQuery
  ): Promise<Paginated<UsageEventView>>;
  usageVerify(
    accessToken: string,
    query?: UsageQuery
  ): Promise<UsageChainVerification>;
  usageExport(accessToken: string, query?: UsageQuery): Promise<string>;
  creditSettlement(
    accessToken: string,
    query?: CreditSettlementQuery
  ): Promise<CreditSettlementReport>;
  creditSettlementExport(
    accessToken: string,
    query?: CreditSettlementQuery
  ): Promise<string>;
  listQuotas(accessToken: string): Promise<QuotaPolicy[]>;

  // ── admin: write (idempotency-key auto-injected by the transport) ──
  inviteUser(
    accessToken: string,
    input: {
      email: string;
      name: string;
      roles?: ServiceRole[];
      idempotencyKey: string;
    }
  ): Promise<InvitedUserResult>;
  createUserWithPassword(
    accessToken: string,
    input: {
      email: string;
      name: string;
      password: string;
      roles?: ServiceRole[];
      idempotencyKey: string;
    }
  ): Promise<CreatedUserResult>;
  updateUserRoles(
    accessToken: string,
    input: { userId: string; roles: ServiceRole[]; idempotencyKey: string }
  ): Promise<void>;
  disableUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void>;
  enableUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void>;
  deleteUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void>;
  grantPlatformAdmin(
    accessToken: string,
    input: {
      userId?: string;
      email?: string;
      name?: string;
      password?: string;
      idempotencyKey: string;
    }
  ): Promise<{ userId: string }>;
  revokePlatformAdmin(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void>;
  listPlatformAdmins(accessToken: string): Promise<PlatformAdminView[]>;
  assignSeat(
    accessToken: string,
    input: {
      userId: string;
      seatStatus: 'assigned' | 'unassigned' | 'suspended';
      idempotencyKey: string;
    }
  ): Promise<void>;
  updateDownloadAccess(
    accessToken: string,
    input: {
      userId: string;
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
      idempotencyKey: string;
    }
  ): Promise<void>;
  createGroup(
    accessToken: string,
    input: { name: string; idempotencyKey: string }
  ): Promise<Group>;
  updateGroupMembers(
    accessToken: string,
    input: {
      groupId: string;
      addUserId?: string;
      removeUserId?: string;
      idempotencyKey: string;
    }
  ): Promise<Group>;
  updateAllowlist(
    accessToken: string,
    input: ModelAccessSubject & { idempotencyKey: string }
  ): Promise<TenantModelAccess>;
  updateTenantLoginDefaultModel(
    accessToken: string,
    input: { loginDefaultModelId: string | null; idempotencyKey: string }
  ): Promise<TenantLoginDefaultModelView>;
  setCustomerModelLabels(
    accessToken: string,
    input: { labels: Record<string, { providerName?: string; modelName?: string }>; idempotencyKey: string }
  ): Promise<Tenant>;
  createModelRoute(
    accessToken: string,
    input: {
      route: ModelRoute;
      catalogItem: VelugaModelCatalogItem;
      idempotencyKey: string;
    }
  ): Promise<ModelRouteAdminView>;
  removeCustomProviderModel(
    accessToken: string,
    input: { catalogModelId: string; idempotencyKey: string }
  ): Promise<void>;
  createGatewayBackend(
    accessToken: string,
    input: { backend: GatewayBackend; idempotencyKey: string }
  ): Promise<void>;
  patchGatewayBackend(
    accessToken: string,
    input: {
      backendId: string;
      backend: Partial<GatewayBackend>;
      idempotencyKey: string;
    }
  ): Promise<void>;
  updateRoutingPolicy(
    accessToken: string,
    input: {
      modelId: string;
      routing: ModelRoutingPolicy;
      idempotencyKey: string;
    }
  ): Promise<void>;
  setQuota(
    accessToken: string,
    input: { quota: QuotaPolicy; idempotencyKey: string }
  ): Promise<void>;
  updateDownloadChannels(
    accessToken: string,
    input: {
      userId: string;
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
      idempotencyKey: string;
    }
  ): Promise<unknown>;
  downloadLatestForUser(
    accessToken: string,
    input: {
      userId: string;
      channel: DownloadChannel;
      platform: DesktopPlatform;
    }
  ): Promise<DownloadManifest>;
  listExtensionReleases(accessToken: string, extensionId: string, platform: 'win32' | 'darwin'): Promise<ExtensionReleaseView[]>;
  promoteExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; version: string; idempotencyKey: string }
  ): Promise<void>;
  rollbackExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; version: string; idempotencyKey: string }
  ): Promise<void>;
  withdrawExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; idempotencyKey: string }
  ): Promise<void>;
  // capability sources (skill-agent-distribution PR10) — platform_admin
  listCapabilitySources(accessToken: string): Promise<CapabilitySourceRecord[]>;
  registerCapabilitySource(
    accessToken: string,
    input: CapabilitySourceRegisterInput & { idempotencyKey: string }
  ): Promise<CapabilitySourceRecord>;
  deleteCapabilitySource(
    accessToken: string,
    id: string,
    idempotencyKey: string
  ): Promise<{ success: boolean }>;
  setPartnerCapabilitySourceScope(
    accessToken: string,
    input: { partnerId: string; sourceIds: string[]; idempotencyKey: string }
  ): Promise<string[]>;
}

export interface CapabilitySourceRegisterInput {
  partnerId?: string;
  sourceKind: 'skill-pack' | 'agent-team' | 'mixed';
  displayName: string;
  description?: string;
  repoUrl: string;
  ref: string;
  indexPath?: string;
  visibility: 'public' | 'private';
  authMethod?: 'github-app' | 'stored-token' | 'none';
  commitPin?: string;
  requireSignature?: boolean;
  requireCommitPin?: boolean;
}

// Re-exports kept for compatibility with downstream consumers.
export type {
  AdminAuditEvent,
  AdminGroupView,
  AdminOverview,
  RouteDecisionEvent,
};

export {
  HttpServiceClient,
  ServiceClientError,
  type HttpServiceClientOptions,
} from './http-client.js';
export {
  InProcessServiceClient,
  type InProcessServiceClientDeps,
} from './in-process-client.js';
export {
  createServiceClient,
  type ServiceClientOptions,
  type ServiceClientTransport,
} from './factory.js';
